import api from './api';

// [需求#6] iOS/Android 添加到主屏幕后台仍能收到系统提示，需要真正的 Web Push
// （Socket.IO 只在网页/App 存活于前台或后台标签页时有效，被系统挂起后收不到）。
// 这里只负责"注册 Service Worker + 订阅 Push + 把订阅信息上报后端"，
// 实际推送内容由后端 server/webpush.js 在事件发生时发送。

export const supportsPush =
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

// VAPID 公钥是 base64url 编码，PushManager.subscribe 需要 Uint8Array 格式
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

// 请求通知权限 + 订阅 Push，返回是否成功。必须在用户手势（点击）内调用，
// 否则 iOS Safari 会静默拒绝权限请求。已经订阅过、或已被拒绝时快速返回，不重复弹窗。
export async function ensurePushSubscription() {
  if (!supportsPush) return false;
  try {
    if (Notification.permission === 'default') {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return false;
    }
    if (Notification.permission !== 'granted') return false;

    const reg = await navigator.serviceWorker.register('/sw.js', {
      updateViaCache: 'none', // 绕过 HTTP 缓存，每次都从服务器取最新 sw.js
    });
    await reg.update(); // 主动触发更新检查，不等浏览器自己在后台检测
    await navigator.serviceWorker.ready;

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const { data } = await api.get('/webpush/vapid-public-key');
      if (!data?.publicKey) return false;
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
    }
    await api.post('/webpush/subscribe', { subscription: sub.toJSON() });
    return true;
  } catch (err) {
    // 静默失败：这是"锦上添花"的后台通道，不应影响主流程或弹错误提示打扰用户
    console.warn('[webpush] 订阅失败:', err);
    return false;
  }
}

// 静默重新确认订阅（无需用户手势）：
// 在 permission 已 granted 的情况下，重新注册 SW 并上报当前设备的 endpoint，
// 用于处理：换设备、清除浏览器数据、重装 PWA 后 DB 里丢失了该设备订阅的情况。
// 不弹任何权限弹窗，静默失败。
export async function silentResubscribe() {
  if (!supportsPush) return;
  if (Notification.permission !== 'granted') return;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', {
      updateViaCache: 'none', // 绕过 HTTP 缓存，每次都从服务器取最新 sw.js
    });
    await reg.update(); // 主动触发更新检查
    await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const { data } = await api.get('/webpush/vapid-public-key');
      if (!data?.publicKey) return;
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
    }
    await api.post('/webpush/subscribe', { subscription: sub.toJSON() });
  } catch (err) {
    console.warn('[webpush] 静默重订阅失败:', err);
  }
}

// 退出登录时取消订阅，避免旧账号在该设备上继续收到推送。
// 静默失败（不影响退出主流程）。
export async function unsubscribePush() {
  if (!supportsPush) return;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const endpoint = sub.endpoint;
    // 先从浏览器侧取消订阅
    await sub.unsubscribe();
    // 再通知后端删掉该 endpoint（fire-and-forget，失败不影响退出）
    api.post('/webpush/unsubscribe', { endpoint }).catch(() => {});
  } catch (err) {
    console.warn('[webpush] 取消订阅失败:', err);
  }
}

export function currentNotifyPermission() {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}
